/**
 * Admin Center – Nutzerverwaltung.
 *
 * Eine Tabelle Person × Bereich. Der eigene Admin-Schalter ist gesperrt, damit
 * sich niemand selbst aussperrt; bei einem Admin sind die Bereichs-Schalter
 * gesperrt, weil ein Admin ohnehin alles sieht. Neue Bereiche erscheinen
 * automatisch als weitere Spalte.
 */
import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatVerwaltung } from './ChatVerwaltung';
import { MontageVerwaltung } from './MontageVerwaltung';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { Modal } from '../komponenten/Modal';
import { Grundeinstellungen } from './EinstellungenModal';
import { Zustaendigkeiten } from './Zustaendigkeiten';
import { KoraVerwaltung } from './KoraVerwaltung';
import type { AdminStand, ChecklistenStand } from '../typen';
import './admin.css';

/**
 * Die Spaltenbreiten der Rechtetabelle.
 *
 * Feste Breiten statt Bruchteilen: Bei zehn Spalten wurde aus `.8fr` ein
 * Streifen, der schmaler war als seine Überschrift – „Buchhaltung" und
 * „Aufträge" schoben sich übereinander. Jetzt behält jede Spalte ihre
 * Breite, und ist kein Platz mehr da, schiebt sich die Tabelle seitwärts.
 * Drei Spalten kommen zu den Bereichen: Vertrieb, Admin, Nur Anzeige.
 */
function raster(bereiche: number): string {
  return `minmax(200px, 1.6fr) repeat(${bereiche + 3}, 80px)`;
}

export function AdminCenterModal() {
  const { modalSchliessen, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const abfrage = useQuery({
    queryKey: ['admin-nutzer'],
    queryFn: () => api.hole<AdminStand>('/admin/nutzer'),
  });

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['admin-nutzer'] });
    void queryClient.invalidateQueries({ queryKey: ['ich'] });
    void queryClient.invalidateQueries({ queryKey: ['meta'] });
  }

  const recht = useMutation({
    mutationFn: ({ id, bereich, erlaubt }: { id: string; bereich: string; erlaubt: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/recht`, { bereich, erlaubt }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  const anzeigeRecht = useMutation({
    mutationFn: ({ id, nurAnzeige }: { id: string; nurAnzeige: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/anzeige`, { nurAnzeige }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-nutzer'] }),
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung fehlgeschlagen.'),
  });

  const adminRecht = useMutation({
    mutationFn: ({ id, istAdmin }: { id: string; istAdmin: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/admin`, { istAdmin }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  const vertrieb = useMutation({
    mutationFn: ({ id, imVertrieb }: { id: string; imVertrieb: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/vertrieb`, { imVertrieb }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  /**
   * Wartungsläufe.
   *
   * Zweistufig: erst zeigen, was passieren würde, dann ausführen. Diese Läufe
   * fassen hunderte Datensätze an – die Vorschau ist der einzige Moment, in
   * dem ein Fehler noch folgenlos ist.
   */
  /*
   * Reiter statt einer langen Rolle.
   *
   * Es ist jetzt der einzige Ort für alles, was einmal für die Firma
   * festgelegt wird – vorher lag die Hälfte in einem zweiten Fenster
   * „Einstellungen". Untereinander wäre das eine Seite, an deren Ende niemand
   * ankommt.
   */
  const [reiter, setReiter] = useState<
    'nutzer' | 'konfiguration' | 'pruefung' | 'chat' | 'montage' | 'wartung' | 'kora'
  >('nutzer');

  const [lauf, setLauf] = useState<string | null>(null);
  const [ausgabe, setAusgabe] = useState('');
  const [ausgefuehrt, setAusgefuehrt] = useState(false);

  const wartungListe = useQuery({
    queryKey: ['wartung'],
    queryFn: () =>
      api.hole<{
        laeufe: {
          schluessel: string;
          titel: string;
          beschreibung: string;
          gefahr: boolean;
          /** Liest nur – dann gibt es keinen Probelauf, weil es nichts vorzuführen gibt. */
          nurLesen: boolean;
        }[];
      }>(
        '/admin/wartung',
      ),
  });

  const wartung = useMutation({
    mutationFn: ({ schluessel, schreiben }: { schluessel: string; schreiben: boolean }) =>
      api.sende<{ ausgabe: string; code: number }>(`/admin/wartung/${schluessel}`, { schreiben }),
    onSuccess: (r, v) => {
      setAusgabe(r.ausgabe);
      setAusgefuehrt(v.schreiben);
      if (v.schreiben) toastZeigen(r.code === 0 ? 'Lauf abgeschlossen.' : 'Lauf mit Fehler beendet.');
    },
    onError: (f) => {
      setAusgabe(f instanceof Error ? f.message : 'Lauf fehlgeschlagen.');
      setAusgefuehrt(false);
    },
  });

  const d = abfrage.data;
  const spalten = d?.bereiche ?? [];

  return (
    <Modal
      titel="Admin Center"
      untertitel="Alles, was einmal für die Firma festgelegt wird – Zugriff, Kalkulation, Texte, Prüfung."
      breite={1100}
      abstandOben={40}
      zeigeSchliessen
      onSchliessen={modalSchliessen}
      kopfZusatz={null}
      fuss={
        <button type="button" className="adm-fertig" onClick={modalSchliessen}>
          Fertig
        </button>
      }
    >
      <div className="adm-reiter">
        {(
          [
            ['nutzer', 'Nutzer & Bereiche'],
            ['konfiguration', 'Konfiguration'],
            ['pruefung', 'Angebotsprüfung'],
            ['chat', 'Chat'],
            ['montage', 'Montage'],
            ['wartung', 'Wartung'],
            ['kora', 'Kora'],
          ] as const
        ).map(([wert, label]) => (
          <button
            key={wert}
            type="button"
            className={`adm-reiter-knopf ${reiter === wert ? 'aktiv' : ''}`}
            onClick={() => setReiter(wert)}
          >
            {/*
              Kora bekommt ihr Gesicht in den Reiter. Sie ist der einzige
              Reiter, hinter dem eine Person steckt und keine Tabelle – und
              das Bild sagt schneller, worum es geht, als das Wort.
            */}
            {wert === 'kora' && (
              <span className="adm-reiter-portrait" aria-hidden="true">
                <img src="/kora/kora-neutral.png" alt="" />
              </span>
            )}
            {label}
          </button>
        ))}
      </div>

      {reiter === 'konfiguration' && (
        <>
          <Zustaendigkeiten />
          <Grundeinstellungen onSchliessen={modalSchliessen} />
        </>
      )}
      {reiter === 'pruefung' && <Checklisten />}
      {reiter === 'chat' && <ChatVerwaltung />}
      {reiter === 'montage' && <MontageVerwaltung />}
      {reiter === 'kora' && <KoraVerwaltung />}

      {reiter === 'nutzer' && !d && (
        <div className="modal-hinweis">Nutzer werden geladen …</div>
      )}

      {reiter === 'nutzer' && d && (
        <>
          <div className="adm-tabelle">
            <div
              className="adm-zeile adm-kopf"
              style={{ gridTemplateColumns: raster(spalten.length) }}
            >
              <div>Person</div>
              {spalten.map((s) => (
                <div key={s.key}>{s.kurz}</div>
              ))}
              {/*
                Vertrieb ist kein Zugriffsrecht, steht aber hier: Es ist die
                zweite Frage zu derselben Person, und für eine eigene Tabelle
                mit denselben fünf Namen gibt es keinen Grund.
              */}
              <div>Vertrieb</div>
              <div>Admin</div>
              <div>Nur Anzeige</div>
            </div>

            {d.nutzer.map((p) => (
              <div
                className="adm-zeile"
                key={p.id}
                style={{ gridTemplateColumns: raster(spalten.length) }}
              >
                <div className="adm-person">
                  <span className="eh-avatar adm-avatar">{p.initialen}</span>
                  <span className="adm-persontext">
                    <span className="adm-name">{p.name}</span>
                    <span className="adm-zugang">{p.zugang}</span>
                  </span>
                </div>

                {spalten.map((s) => {
                  const gesperrt = p.gesperrt.bereiche;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      title={
                        s.label + (gesperrt ? ' (Admin sieht immer alles)' : '')
                      }
                      className={`adm-schalter ${p.rechte[s.key] ? 'an' : ''} ${gesperrt ? 'gesperrt' : ''}`}
                      onClick={() =>
                        !gesperrt &&
                        recht.mutate({ id: p.id, bereich: s.key, erlaubt: !p.rechte[s.key] })
                      }
                    >
                      <span />
                    </button>
                  );
                })}

                <button
                  type="button"
                  title="Vertrieb · schreibt und versendet Angebote, steht im Team-Überblick, Kora spricht über sie"
                  className={`adm-schalter ${p.imVertrieb ? 'an' : ''}`}
                  onClick={() => vertrieb.mutate({ id: p.id, imVertrieb: !p.imVertrieb })}
                >
                  <span />
                </button>

                <button
                  type="button"
                  title={
                    'Admin · Einstellungen und Rechte' +
                    (p.gesperrt.admin ? ' (eigener Zugang gesperrt)' : '')
                  }
                  className={`adm-schalter ${p.istAdmin ? 'an' : ''} ${p.gesperrt.admin ? 'gesperrt' : ''}`}
                  onClick={() =>
                    !p.gesperrt.admin && adminRecht.mutate({ id: p.id, istAdmin: !p.istAdmin })
                  }
                >
                  <span />
                </button>

                {/*
                  Nur Anzeige: der Bildschirm im Büro (MONTAGEKALENDER.md
                  6.1). Er schließt sich mit Admin gegenseitig aus – ein
                  Gerät an der Wand darf nie Rechte vergeben.
                */}
                <button
                  type="button"
                  title={
                    'Nur Anzeige (Bildschirm) · sieht ausschließlich den Montagekalender' +
                    (p.gesperrt.anzeige ? ' (für Administratoren und den eigenen Zugang gesperrt)' : '')
                  }
                  className={`adm-schalter ${p.nurAnzeige ? 'an' : ''} ${p.gesperrt.anzeige ? 'gesperrt' : ''}`}
                  onClick={() =>
                    !p.gesperrt.anzeige && anzeigeRecht.mutate({ id: p.id, nurAnzeige: !p.nurAnzeige })
                  }
                >
                  <span />
                </button>
              </div>
            ))}
          </div>

          <div className="modal-hinweis">
            Admin sieht immer alle Bereiche und darf Rechte vergeben. Der eigene Admin-Schalter ist
            gesperrt, damit sich niemand selbst aussperrt. Neue Bereiche erscheinen hier automatisch
            als weitere Spalte.
          </div>

          <div className="modal-hinweis">
            „Vertrieb" ist kein Zugriffsrecht: Wer aus ist, behält seine Bereiche, steht aber nicht
            mehr als Versender oder Kalkulant zur Auswahl, taucht nicht im Team-Überblick auf und
            Kora sagt nichts mehr über offene Vorgänge dieser Person. Solange noch laufende Angebote
            auf ihren Namen gehen, lässt sich der Schalter nicht umlegen – sonst stünden diese
            Vorgänge auf niemandes Tisch.
          </div>

          <div className="modal-hinweis">
            Neue Personen bekommen ihren Zugang über das Microsoft-Konto von Eichenherz. Damit die
            Anmeldung greift, muss die E-Mail-Adresse hier hinterlegt sein.
          </div>

          <NutzerAnlegen onFertig={neuLaden} />

          <div className="modal-hinweis">
            „Bildschirm" ist für ein Gerät an der Wand, nicht für eine Person: Das Konto sieht nur
            den Montagekalender, ändert nichts und steht in keinem Team-Überblick.
          </div>

        </>
      )}

      {/*
        Wartungsläufe. Sie leben sonst nur in der Kommandozeile – auf dem
        Server gibt es keine, und an die Datenbank der Produktion kommt man
        von außen nicht heran. Ohne diesen Zugang wären sie genau dort
        unerreichbar, wo sie gebraucht werden.
      */}
      {reiter === 'wartung' && (
          <div className="adm-abgleich">
            <div className="adm-abgleich-titel">Wartung</div>
            <div className="adm-abgleich-text">
              Von oben nach unten abarbeiten – so ist die Reihenfolge beim Go-live gedacht. Jeder
              Lauf zeigt zuerst, was er tun würde; geschrieben wird erst mit dem zweiten Klick.
            </div>

            <div className="adm-laeufe">
              {(wartungListe.data?.laeufe ?? []).map((l) => (
                <div
                  className={`adm-lauf ${lauf === l.schluessel ? 'aktiv' : ''} ${l.gefahr ? 'gefahr' : ''}`}
                  key={l.schluessel}
                >
                  <div className="adm-lauf-kopf">
                    <div>
                      <div className="adm-lauf-titel">
                        {l.titel}
                        {l.gefahr && <span className="adm-lauf-marke">Vorsicht</span>}
                      </div>
                      <div className="adm-abgleich-text">{l.beschreibung}</div>
                    </div>
                    {/*
                      „Prüfen", nicht „Vorschau": Der Lauf liest die
                      angebundenen Quellen und rechnet – das dauert und ist
                      kein Blick auf ein fertiges Ergebnis. Er schreibt nur nichts.
                    */}
                    <button
                      type="button"
                      className="adm-abgleich-knopf"
                      disabled={wartung.isPending}
                      onClick={() => {
                        setLauf(l.schluessel);
                        setAusgabe('');
                        setAusgefuehrt(false);
                        wartung.mutate({ schluessel: l.schluessel, schreiben: false });
                      }}
                    >
                      {wartung.isPending && lauf === l.schluessel
                        ? 'Prüft …'
                        : l.nurLesen
                          ? 'Prüfen'
                          : 'Vorschau'}
                    </button>
                  </div>

                  {lauf === l.schluessel && ausgabe && (
                    <>
                      <pre className="adm-ausgabe">{ausgabe}</pre>
                      {/*
                        Ein Lauf, der nur liest, hat kein zweites Mal: Was er
                        anzeigt, ist bereits das Ergebnis. Ein Knopf „Ausführen"
                        daneben würde behaupten, es käme noch etwas.
                      */}
                      {!ausgefuehrt && !l.nurLesen && (
                        <button
                          type="button"
                          className={`adm-abgleich-knopf betont ${l.gefahr ? 'gefahr' : ''}`}
                          disabled={wartung.isPending}
                          onClick={() => {
                            /*
                              Ein Lauf, der löscht, verdient eine Rückfrage, die
                              man nicht wegklickt, ohne sie gelesen zu haben.
                              Die übrigen Läufe legen an oder tragen nach – ein
                              Fehlgriff dort ist ärgerlich, aber nicht endgültig.
                            */
                            if (
                              l.gefahr &&
                              !window.confirm(
                                'Alle Angebote, Kunden und Verläufe aus Kora löschen?\n\n' +
                                  'Die Ablage bleibt unberührt. Der Schritt lässt sich ' +
                                  'nicht rückgängig machen.',
                              )
                            ) {
                              return;
                            }
                            wartung.mutate({ schluessel: l.schluessel, schreiben: true });
                          }}
                        >
                          {l.gefahr ? 'Endgültig löschen' : 'Ausführen'}
                        </button>
                      )}
                      {ausgefuehrt && <div className="adm-abgleich-text">Ausgeführt.</div>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Prüf-Checklisten je Gewerk.
 *
 * Eine Fassung wird nie gelöscht, nur abgelöst: Prüfungen verweisen auf die
 * Fassung, mit der sie gelaufen sind. Ohne sie ließe sich ein alter Prüfbericht
 * später nicht mehr lesen, weil die Kriterien inzwischen andere sind.
 */
function Checklisten() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState('');

  const abfrage = useQuery({
    queryKey: ['checklisten'],
    queryFn: () => api.hole<ChecklistenStand>('/pruefung/checklisten'),
  });

  const hochladen = useMutation({
    mutationFn: ({ prefix, datei }: { prefix: string; datei: File }) => {
      const formular = new FormData();
      formular.append('kategorie', prefix);
      formular.append('datei', datei);
      return api.datei<{ toast: string }>('/pruefung/checklisten', formular);
    },
    onSuccess: (a) => {
      setFehler('');
      setLaeuft('');
      void queryClient.invalidateQueries({ queryKey: ['checklisten'] });
      toastZeigen(a.toast);
    },
    onError: (f) => {
      setLaeuft('');
      setFehler(f instanceof ApiFehler ? f.message : 'Die Datei konnte nicht abgelegt werden.');
    },
  });

  const daten = abfrage.data;

  return (
    <div className="adm-block">
      <div className="adm-titel">Prüf-Checklisten</div>
      <div className="adm-hinweis">
        Je Gewerk eine PDF mit den Punkten, auf die geprüft werden soll. Kora legt sie beim Prüfen
        zusammen mit dem Angebot vor. Ohne Checkliste bleibt die Statussperre für dieses Gewerk
        aus – ein Vorgang, den niemand prüfen kann, darf nicht blockiert sein.
      </div>

      {daten && !daten.kiAktiv && (
        /* Ein Hinweis, keine Warnung – dieselbe Gestalt wie überall (v12, Punkt 6). */
        <div className="eh-hinweisblock">
          Der Zugang zur Prüfung ist noch nicht hinterlegt (ANTHROPIC_API_KEY). Bis dahin lässt sich
          nichts prüfen.
        </div>
      )}

      <div className="adm-checklisten">
        {(daten?.kategorien ?? []).map((k) => (
          <div className="adm-checkliste" key={k.prefix}>
            <div className="adm-checkliste-kopf">
              <span className="adm-checkliste-name">
                {k.prefix} · {k.label}
              </span>
              <label className="adm-abgleich-knopf">
                {laeuft === k.prefix ? 'Wird abgelegt …' : k.aktuell ? 'Neue Fassung' : 'PDF wählen'}
                <input
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  disabled={hochladen.isPending}
                  onChange={(e) => {
                    const datei = e.target.files?.[0];
                    e.target.value = '';
                    if (!datei) return;
                    setFehler('');
                    setLaeuft(k.prefix);
                    hochladen.mutate({ prefix: k.prefix, datei });
                  }}
                />
              </label>
            </div>

            {k.aktuell ? (
              <div className="adm-checkliste-stand">
                <a
                  href={`/api/pruefung/checklisten/${k.aktuell.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {k.aktuell.dateiname}
                </a>
                <span>
                  seit {new Date(k.aktuell.hochgeladenAm).toLocaleDateString('de-AT')} ·{' '}
                  {k.aktuell.hochgeladenVon}
                  {k.fassungen.length > 1 ? ` · ${k.fassungen.length} Fassungen` : ''}
                </span>
              </div>
            ) : (
              <div className="adm-checkliste-stand leer">noch keine Checkliste</div>
            )}
          </div>
        ))}
      </div>

      {fehler && <div className="eh-fehler">{fehler}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Eine Person – oder einen Bildschirm – anlegen.
 *
 * Den Zugang selbst vergibt Microsoft; hier entsteht nur die Person in Kora.
 * Ohne diesen Eintrag läuft eine Anmeldung ins Leere, denn Kora erkennt sie
 * an der E-Mail-Adresse. Bis hierher gab es die Anlage nur als Route ohne
 * Bedienung – der Bildschirm im Büro ließ sich damit gar nicht einrichten.
 */
function NutzerAnlegen({ onFertig }: { onFertig: () => void }) {
  const { toastZeigen } = useKora();
  const [offen, setOffen] = useState(false);
  const [art, setArt] = useState<'person' | 'bildschirm'>('person');
  const [feld, setFeld] = useState({ name: '', anzeigename: '', email: '' });

  const leeren = () => {
    setFeld({ name: '', anzeigename: '', email: '' });
    setArt('person');
    setOffen(false);
  };

  const anlegen = useMutation({
    mutationFn: () =>
      api.sende<{ id: string }>('/admin/nutzer', {
        name: feld.name.trim(),
        anzeigename: feld.anzeigename.trim() || feld.name.trim(),
        email: feld.email.trim(),
        nurAnzeige: art === 'bildschirm',
      }),
    onSuccess: () => {
      toastZeigen(
        art === 'bildschirm'
          ? `${feld.name.trim()} angelegt – nur Anzeige, Montagekalender freigeschaltet.`
          : `${feld.name.trim()} angelegt. Bereiche jetzt in der Tabelle freigeben.`,
      );
      leeren();
      onFertig();
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Anlegen fehlgeschlagen.'),
  });

  const gueltig = feld.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(feld.email.trim());

  if (!offen) {
    return (
      <button type="button" className="adm-anlegen-auf" onClick={() => setOffen(true)}>
        + Person oder Bildschirm hinzufügen
      </button>
    );
  }

  return (
    <div className="adm-anlegen">
      <div className="adm-anlegen-arten">
        {(['person', 'bildschirm'] as const).map((a) => (
          <button
            key={a}
            type="button"
            className={`adm-anlegen-art ${art === a ? 'aktiv' : ''}`}
            onClick={() => setArt(a)}
          >
            {a === 'person' ? 'Person' : 'Bildschirm'}
          </button>
        ))}
      </div>

      <div className="adm-anlegen-felder">
        <label className="adm-anlegen-feld">
          <span>Name in der Liste</span>
          <input
            value={feld.name}
            placeholder={art === 'bildschirm' ? 'Bildschirm' : 'Vorname'}
            onChange={(e) => setFeld((x) => ({ ...x, name: e.target.value }))}
          />
        </label>
        <label className="adm-anlegen-feld">
          <span>Voller Name</span>
          <input
            value={feld.anzeigename}
            placeholder={art === 'bildschirm' ? 'Bildschirm Büro' : 'Vorname Nachname'}
            onChange={(e) => setFeld((x) => ({ ...x, anzeigename: e.target.value }))}
          />
        </label>
        <label className="adm-anlegen-feld">
          <span>E-Mail</span>
          <input
            value={feld.email}
            placeholder={art === 'bildschirm' ? 'screen@eichenherz.at' : 'vorname.nachname@eichenherz.at'}
            onChange={(e) => setFeld((x) => ({ ...x, email: e.target.value }))}
          />
        </label>
      </div>

      <div className="adm-anlegen-fuss">
        <span className="adm-anlegen-note">
          {art === 'bildschirm'
            ? 'Sieht nur den Montagekalender und ändert nichts.'
            : 'Bereiche werden danach in der Tabelle freigegeben.'}
        </span>
        <span className="adm-anlegen-knoepfe">
          <button type="button" className="adm-anlegen-weg" onClick={leeren}>
            Abbrechen
          </button>
          <button
            type="button"
            className="adm-fertig adm-anlegen-los"
            disabled={!gueltig || anlegen.isPending}
            onClick={() => anlegen.mutate()}
          >
            {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
          </button>
        </span>
      </div>
    </div>
  );
}
